import PropTypes from 'prop-types';
import { AlertTriangle, LogOut, Trash2, X } from 'lucide-react';
import { Heading, Text, Stack } from '@/components/ui/index.js';
import styles from './ConfirmDialog.module.css';

/**
 * ConfirmDialog - Modal dialog for confirmations
 * Used for destructive actions like logout and delete account
 */
export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel,
  cancelLabel = 'Cancel',
  variant = 'default',
  isLoading = false,
}) {
  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const icons = {
    default: AlertTriangle,
    logout: LogOut,
    delete: Trash2,
  };

  const Icon = icons[variant] || AlertTriangle;

  return (
    <div
      className={styles.overlay}
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="dialog-title"
    >
      <div className={styles.dialog}>
        <button
          type="button"
          onClick={onClose}
          className={styles.closeButton}
          aria-label="Close dialog"
        >
          <X size={20} />
        </button>

        <Stack direction="column" gap={4} align="center" className={styles.content}>
          <div className={`${styles.iconContainer} ${styles[variant]}`}>
            <Icon size={28} />
          </div>

          <Stack direction="column" gap={2} align="center">
            <Heading
              level={3}
              size="md"
              color="primary"
              id="dialog-title"
            >
              {title}
            </Heading>
            <Text size="sm" color="muted" align="center">
              {message}
            </Text>
          </Stack>
        </Stack>

        <Stack direction="column" gap={3} className={styles.actions}>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isLoading}
            className={`${styles.confirmButton} ${styles[variant]}`}
          >
            {isLoading ? (
              <span className={styles.spinner} />
            ) : (
              confirmLabel
            )}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className={styles.cancelButton}
          >
            {cancelLabel}
          </button>
        </Stack>
      </div>
    </div>
  );
}

ConfirmDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  confirmLabel: PropTypes.string.isRequired,
  cancelLabel: PropTypes.string,
  variant: PropTypes.oneOf(['default', 'logout', 'delete']),
  isLoading: PropTypes.bool,
};

export default ConfirmDialog;
